import React, { useEffect, useRef, useState } from "react";
import { ArrowUpRight, Mail } from "lucide-react";
import { LINKS } from "../../lib/constants";

// MailerLite endpoint provided by user
const ML_SUBSCRIBE_URL = "https://assets.mailerlite.com/jsonp/2297901/forms/187387488308823899/subscribe";
const ML_TAKEL_URL = "https://assets.mailerlite.com/jsonp/2297901/forms/187387488308823899/takel";

export default function NewsletterForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const formRef = useRef(null);

  useEffect(() => {
    // Fire MailerLite "form loaded" tracking ping (mirrors their embed)
    fetch(ML_TAKEL_URL, { mode: "no-cors" }).catch(() => {});
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      setStatus("error");
      return;
    }
    setStatus("loading");

    const formData = new FormData();
    formData.append("fields[name]", name);
    formData.append("fields[email]", email);
    formData.append("ml-submit", "1");
    formData.append("anticsrf", "true");

    try {
      // MailerLite accepts form-encoded POST via no-cors (opaque success)
      await fetch(ML_SUBSCRIBE_URL, {
        method: "POST",
        body: formData,
        mode: "no-cors",
      });
      setStatus("success");
      setName("");
      setEmail("");
    } catch {
      setStatus("error");
    }
  };

  return (
    <section
      data-testid="newsletter-section"
      className="bg-brand-black py-20 lg:py-24 border-y-[1.5px] border-brand-black"
    >
      <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6">
          <span className="section-label on-dark">Stay in the loop</span>
          <h2 className="font-display text-4xl sm:text-5xl mt-6 leading-[1] text-white">
            Calm money notes<br />
            for business owners<br />
            who are <span className="text-brand-yellow">done guessing.</span>
          </h2>
          <p className="mt-6 text-[17px] leading-relaxed text-white/85 max-w-md">
            Occasional, thoughtful notes from me on profit clarity, cashflow, the Follow The Cash™ method, forecastly updates, and building a business that supports your life. No spam. No hustle culture. No pressure.
          </p>
        </div>

        <div className="lg:col-span-6">
          {status === "success" ? (
            <div
              className="bg-brand-yellow border-[1.5px] border-brand-black rounded-[10px] p-8 text-center"
              data-testid="newsletter-success"
            >
              <Mail size={36} className="mx-auto text-brand-black mb-3" />
              <h3 className="font-display text-3xl mb-2">Thank you!</h3>
              <p className="text-brand-black/85 text-[15px]">
                You've successfully joined the list. Check your inbox for a welcome note.
              </p>
            </div>
          ) : (
            <form
              ref={formRef}
              onSubmit={onSubmit}
              className="bg-white border-[1.5px] border-brand-black rounded-[10px] p-7"
              data-testid="newsletter-form"
            >
              <h3 className="font-display text-2xl mb-4 uppercase">Newsletter</h3>
              <p className="text-sm text-brand-stone mb-5">Stay connected with me at ProfitMIND.</p>

              <div className="space-y-4">
                <div>
                  <label htmlFor="newsletter-name" className="sr-only">First name</label>
                  <input
                    id="newsletter-name"
                    type="text"
                    placeholder="First name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    autoComplete="given-name"
                    data-testid="newsletter-input-name"
                    className="w-full px-4 py-3.5 bg-white border-[1.5px] border-brand-black rounded-md font-sans text-sm focus:outline-none focus:ring-[3px] focus:ring-brand-yellow"
                  />
                </div>
                <div>
                  <label htmlFor="newsletter-email" className="sr-only">Email</label>
                  <input
                    id="newsletter-email"
                    type="email"
                    placeholder="Email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    data-testid="newsletter-input-email"
                    className="w-full px-4 py-3.5 bg-white border-[1.5px] border-brand-black rounded-md font-sans text-sm focus:outline-none focus:ring-[3px] focus:ring-brand-yellow"
                  />
                </div>
                <button
                  type="submit"
                  disabled={status === "loading"}
                  data-testid="newsletter-submit-btn"
                  className="btn-yellow w-full disabled:opacity-60"
                >
                  {status === "loading" ? "Subscribing…" : (<>Join the list <ArrowUpRight size={16} /></>)}
                </button>
                {status === "error" && (
                  <p className="text-xs text-red-600" data-testid="newsletter-error">
                    Something went wrong — please check your email and try again, or email{" "}
                    <a href={`mailto:${LINKS.email}`} className="underline">{LINKS.email}</a>.
                  </p>
                )}
              </div>

              <p className="text-[11px] text-brand-stone mt-5 leading-relaxed">
                You can unsubscribe anytime. For more details, review our{" "}
                <a href="/privacy-policy" className="underline decoration-brand-yellow decoration-2 underline-offset-2">Privacy Policy</a>.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
